import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { SpeedInsights } from "@vercel/speed-insights/react";
import "@/App.css";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import CookieConsent from "./components/layout/CookieConsent";

const HomePage = lazy(() => import("./pages/HomePage"));
const BungalowsPage = lazy(() => import("./pages/BungalowsPage"));
const NewConstructionPage = lazy(() => import("./pages/NewConstructionPage"));
const BuilderAccessPage = lazy(() => import("./pages/BuilderAccessPage"));
const ToolsHub = lazy(() => import("./pages/ToolsHub"));
const CalculatorPage = lazy(() => import("./pages/CalculatorPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const IntellectualPropertyPage = lazy(() => import("./pages/IntellectualPropertyPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const TrustTransparency = lazy(() => import("./pages/TrustTransparency"));
const FoundersNote = lazy(() => import("./pages/FoundersNote"));

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' }));
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Layout = ({ children, hideNav = false, hideFooter = false }) => (
  <>
    {!hideNav && <Navbar />}
    <main>{children}</main>
    {!hideNav && !hideFooter && <Footer />}
  </>
);

const AdminRoute = () => (
  <>
    <Helmet>
      <title>Private Office | DownsizeGTA</title>
      <meta name="robots" content="noindex,nofollow,noarchive" />
      <meta name="googlebot" content="noindex,nofollow,noarchive" />
    </Helmet>
    <Layout hideNav><AdminDashboard /></Layout>
  </>
);

const NoIndexUtilityRoute = ({ children }) => (
  <>
    <Helmet>
      <meta name="robots" content="noindex,follow" />
      <meta name="googlebot" content="noindex,follow" />
    </Helmet>
    <Layout>{children}</Layout>
  </>
);

const PageLoader = () => (
  <div className="min-h-screen bg-[#0f1729] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <CookieConsent />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/bungalows" element={<Layout hideFooter><BungalowsPage /></Layout>} />
            <Route path="/new-construction" element={<Layout hideFooter><NewConstructionPage /></Layout>} />
            <Route path="/builder-access" element={<Layout hideFooter><BuilderAccessPage /></Layout>} />
            <Route path="/new-builds" element={<Navigate to="/new-construction" replace />} />
            <Route path="/decision-review" element={<Navigate to="/" replace />} />
            <Route path="/tools" element={<Layout><ToolsHub /></Layout>} />
            <Route path="/calculator" element={<Layout><CalculatorPage /></Layout>} />
            <Route path="/trust-transparency" element={<Layout><TrustTransparency /></Layout>} />
            <Route path="/founders-note" element={<Layout><FoundersNote /></Layout>} />
            <Route path="/ip-notice" element={<Layout><IntellectualPropertyPage /></Layout>} />
            <Route path="/privacy-policy" element={<NoIndexUtilityRoute><PrivacyPolicy /></NoIndexUtilityRoute>} />
            <Route path="/terms-of-use" element={<NoIndexUtilityRoute><TermsOfUse /></NoIndexUtilityRoute>} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <SpeedInsights />
      </Router>
    </HelmetProvider>
  );
}

export default App;
